// ============================================================================
// GumaHUB Login Page - Enterprise Design
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, api } from '@gumahub/shared';
import { useAuth } from '../hooks/useAuth.js';
import { LegalDialog } from '../components/LegalDialog.js';

interface DemoUser {
  userId: string;
  username: string;
  displayName: string;
  isPlatformAdmin: boolean;
  clientName?: string;
}

// Demo password used for all demo accounts
const DEMO_PASSWORD = 'Admin123!';

// Color mappings based on user role/client
const getColorClasses = (user: DemoUser) => {
  if (user.isPlatformAdmin) {
    return {
      bgColor: 'bg-guma-50 hover:bg-guma-100 border-guma-200',
      textColor: 'text-guma-700'
    };
  }
  if (user.clientName?.toLowerCase().includes('pepsi') || user.username.includes('supplier')) {
    return {
      bgColor: 'bg-ocean-50 hover:bg-ocean-100 border-ocean-200',
      textColor: 'text-ocean-700'
    };
  }
  if (user.clientName?.toLowerCase().includes('aptr') || user.username.includes('customer')) {
    return {
      bgColor: 'bg-mint-50 hover:bg-mint-100 border-mint-200',
      textColor: 'text-mint-700'
    };
  }
  return {
    bgColor: 'bg-amber-50 hover:bg-amber-100 border-amber-200',
    textColor: 'text-amber-700'
  };
};

export const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginInProgress, setLoginInProgress] = useState<string | null>(null);
  const [demoUsers, setDemoUsers] = useState<DemoUser[]>([]);
  const [legalOpen, setLegalOpen] = useState<'terms' | 'privacy' | null>(null);
  // Demo quick-login is hidden in production builds (promote-to-prod sets this).
  const hideDemo = import.meta.env.VITE_HIDE_DEMO_LOGIN === 'true';

  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');

  // Get redirect path from location state
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  // Fetch demo users on mount
  useEffect(() => {
    const fetchDemoUsers = async () => {
      try {
        const response = await api.get<DemoUser[]>('/auth/users/demo');
        if (response.success && response.data) {
          setDemoUsers(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch demo users:', err);
      }
    };
    fetchDemoUsers();
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Clear error when inputs change
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [username, password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      return;
    }

    const success = await login(username, password);
    if (success) {
      navigate(from, { replace: true });
    }
  };

  const handleDemoLogin = async (demoUser: DemoUser) => {
    setLoginInProgress(demoUser.username);
    const success = await login(demoUser.username, DEMO_PASSWORD);
    if (success) {
      navigate(from, { replace: true });
    }
    setLoginInProgress(null);
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Background with animated gradient mesh */}
      <div className="absolute inset-0 bg-slate-900">
        {/* Gradient orbs */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-guma-500/20 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-ocean-500/20 rounded-full blur-[100px] translate-x-1/4 translate-y-1/4" />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-mint-500/10 rounded-full blur-[80px] -translate-x-1/2 -translate-y-1/2" />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Left panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative z-10 flex-col justify-between p-12">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-guma-500 to-guma-600 flex items-center justify-center shadow-xl shadow-guma-500/30">
            <img
              src="https://www.gumahub.com/assets/gumahub-Hx4eram0.png"
              alt="GumaHUB"
              className="h-7 brightness-0 invert"
            />
          </div>
          <div>
            <span className="text-xl font-bold text-white">GumaHUB</span>
            <span className="block text-xs font-medium text-slate-400 uppercase tracking-wider">Enterprise Platform</span>
          </div>
        </div>

        {/* Hero content */}
        <div className="max-w-lg">
          <h1 className="text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
            {t('hero.title.part1')}{' '}
            <span className="bg-gradient-to-r from-guma-400 via-amber-400 to-ocean-400 bg-clip-text text-transparent">
              {t('hero.title.highlight')}
            </span>{' '}
            {t('hero.title.part2')}
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* Feature highlights */}
          <div className="mt-10 grid grid-cols-2 gap-6">
            {[
              { icon: '01', labelKey: 'hero.features.enterprise', color: 'bg-guma-500' },
              { icon: '02', labelKey: 'hero.features.orders', color: 'bg-ocean-500' },
              { icon: '03', labelKey: 'hero.features.shipments', color: 'bg-mint-500' },
              { icon: '04', labelKey: 'hero.features.bidding', color: 'bg-amber-400' },
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 group">
                <div className={`w-10 h-10 rounded-xl ${feature.color} bg-opacity-20 flex items-center justify-center`}>
                  <span className={`text-sm font-bold ${feature.color.replace('bg-', 'text-')}`}>{feature.icon}</span>
                </div>
                <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
                  {t(feature.labelKey)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-mint-500 animate-pulse" />
            <span className="text-sm text-slate-500">{t('hero.systemStatus')}</span>
          </div>
          <div className="w-px h-4 bg-slate-700" />
          <span className="text-sm text-slate-500">{t('hero.security')}</span>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-3 mb-8 lg:hidden">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-guma-500 to-guma-600 flex items-center justify-center shadow-xl shadow-guma-500/30">
              <img
                src="https://www.gumahub.com/assets/gumahub-Hx4eram0.png"
                alt="GumaHUB"
                className="h-7 brightness-0 invert"
              />
            </div>
            <div>
              <span className="text-xl font-bold text-white">GumaHUB</span>
              <span className="block text-xs font-medium text-slate-400 uppercase tracking-wider">Enterprise Platform</span>
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-3xl shadow-2xl shadow-black/20 overflow-hidden">
            {/* Card header with gradient accent */}
            <div className="h-1.5 bg-gradient-to-r from-guma-500 via-amber-400 via-mint-500 to-ocean-500" />

            <div className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900">
                  {t('login.title')}
                </h2>
                <p className="text-slate-500 mt-2">
                  {t('login.subtitle')}
                </p>
              </div>

              {error && (
                <Alert type="error" className="mb-6">
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    {t('login.email')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder={t('login.emailPlaceholder')}
                      autoComplete="username"
                      required
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-guma-500 focus:ring-4 focus:ring-guma-500/10 transition-all duration-200"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-slate-700">
                      {t('login.password')}
                    </label>
                    <button type="button" className="text-sm font-medium text-guma-600 hover:text-guma-700 transition-colors">
                      {t('login.forgotPassword')}
                    </button>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('login.passwordPlaceholder')}
                      autoComplete="current-password"
                      required
                      className="w-full pl-12 pr-12 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-guma-500 focus:ring-4 focus:ring-guma-500/10 transition-all duration-200"
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 px-6 bg-gradient-to-r from-guma-500 to-guma-600 hover:from-guma-600 hover:to-guma-700 text-white font-semibold rounded-xl shadow-lg shadow-guma-500/30 hover:shadow-xl hover:shadow-guma-500/40 focus:outline-none focus:ring-4 focus:ring-guma-500/30 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>{t('login.signingIn')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('login.signIn')}</span>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
              </form>

              {/* Demo Quick Login (hidden in production) */}
              {!hideDemo && demoUsers.length > 0 && (
                <div className="mt-8 pt-6 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 text-center mb-4 uppercase tracking-wider">
                    {t('login.quickDemo')}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {demoUsers.map((demoUser) => {
                      const colors = getColorClasses(demoUser);
                      return (
                        <button
                          key={demoUser.username}
                          type="button"
                          onClick={() => handleDemoLogin(demoUser)}
                          disabled={isLoading || loginInProgress !== null}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all duration-200 ${colors.bgColor} ${colors.textColor} disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95`}
                          title={demoUser.clientName ? `${demoUser.displayName} (${demoUser.clientName})` : demoUser.displayName}
                        >
                          {loginInProgress === demoUser.username ? (
                            <span className="flex items-center gap-1.5">
                              <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              {t('common:loading')}
                            </span>
                          ) : demoUser.displayName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer text */}
          <p className="text-center text-sm text-slate-500 mt-8">
            {t('login.agreementPrefix')}{' '}
            <button type="button" onClick={() => setLegalOpen('terms')} className="text-guma-400 hover:text-guma-300 font-medium transition-colors underline-offset-2 hover:underline">{t('login.termsOfService')}</button>
            {' '}{t('login.and')}{' '}
            <button type="button" onClick={() => setLegalOpen('privacy')} className="text-guma-400 hover:text-guma-300 font-medium transition-colors underline-offset-2 hover:underline">{t('login.privacyPolicy')}</button>
          </p>
        </div>
      </div>

      {legalOpen && <LegalDialog kind={legalOpen} onClose={() => setLegalOpen(null)} />}
    </div>
  );
};

export default LoginPage;
